import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PERF } from "../constants/testIds2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Target, Star } from "@phosphor-icons/react";

const STATUS = ["on_track", "at_risk", "completed"];
const STATUS_STYLE = {
  on_track: "bg-emerald-50 text-emerald-800 border-emerald-200",
  at_risk: "bg-orange-50 text-orange-800 border-orange-200",
  completed: "bg-blue-50 text-blue-800 border-blue-200",
};

export default function Performance() {
  const { user, tenantId } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [goals, setGoals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openCycle, setOpenCycle] = useState(false);
  const [openGoal, setOpenGoal] = useState(false);
  const [cycleForm, setCycleForm] = useState({ name: "", start_date: "", end_date: "" });
  const [goalForm, setGoalForm] = useState({ cycle_id: "", employee_id: "", title: "", description: "", weight_percent: 25, target: "" });
  const isAdmin = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/performance/cycles", { params }).then(({ data }) => {
      setCycles(data);
      if (data.length && !selectedCycle) setSelectedCycle(data[0].id);
    });
    if (isAdmin) api.get("/employees", { params }).then(({ data }) => setEmployees(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  useEffect(() => {
    if (!selectedCycle) return;
    if (isAdmin) {
      const params = { cycle_id: selectedCycle };
      if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
      api.get("/performance/goals", { params }).then(({ data }) => setGoals(data));
    } else if (user?.employee_id) {
      api.get("/performance/goals/mine", { params: { cycle_id: selectedCycle } }).then(({ data }) => setGoals(data));
    }
  }, [selectedCycle, tenantId, isAdmin, user]);

  const submitCycle = async () => {
    try {
      const payload = { ...cycleForm };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/performance/cycles", payload);
      toast.success("Cycle created");
      setOpenCycle(false); setCycleForm({ name: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitGoal = async () => {
    try {
      const payload = { ...goalForm, weight_percent: Number(goalForm.weight_percent) };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/performance/goals", payload);
      toast.success("Goal added");
      setOpenGoal(false); setGoalForm({ cycle_id: selectedCycle, employee_id: "", title: "", description: "", weight_percent: 25, target: "" });
      // reload
      const params = { cycle_id: selectedCycle };
      if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
      api.get("/performance/goals", { params }).then(({ data }) => setGoals(data));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateGoal = async (gid, patch) => {
    try { await api.patch(`/performance/goals/${gid}`, patch); setGoals(goals.map((g) => g.id === gid ? { ...g, ...patch } : g)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const selectedCycleObj = cycles.find((c) => c.id === selectedCycle);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Talent</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Performance</h1>
          <p className="text-sm text-zinc-500 mt-2">Review cycles, goals, and ratings.</p>
        </div>
        <div className="flex items-end gap-2">
          {cycles.length > 0 && (
            <div>
              <Label className="text-xs">Cycle</Label>
              <Select value={selectedCycle} onValueChange={setSelectedCycle}>
                <SelectTrigger className="w-[200px] rounded-sm" data-testid="cycle-select"><SelectValue /></SelectTrigger>
                <SelectContent>{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {["super_admin", "company_admin", "hr"].includes(user?.role) && (
            <Dialog open={openCycle} onOpenChange={setOpenCycle}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid={PERF.cycleCreateBtn}><Plus size={16} className="mr-1.5" /> Cycle</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl">Create review cycle</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Name (e.g. Q2 2026)</Label><Input value={cycleForm.name} onChange={(e) => setCycleForm({ ...cycleForm, name: e.target.value })} className="rounded-sm" data-testid="cycle-name-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="date" value={cycleForm.start_date} onChange={(e) => setCycleForm({ ...cycleForm, start_date: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">End</Label><Input type="date" value={cycleForm.end_date} onChange={(e) => setCycleForm({ ...cycleForm, end_date: e.target.value })} className="rounded-sm" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenCycle(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitCycle} className="btn-zenith rounded-sm" data-testid="cycle-save-btn">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && selectedCycle && (
            <Dialog open={openGoal} onOpenChange={(v) => { setOpenGoal(v); if (v) setGoalForm({ ...goalForm, cycle_id: selectedCycle }); }}>
              <DialogTrigger asChild>
                <Button className="btn-zenith rounded-sm" data-testid={PERF.goalCreateBtn}><Plus size={16} className="mr-1.5" /> Goal</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl">Add goal</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                    <Select value={goalForm.employee_id} onValueChange={(v) => setGoalForm({ ...goalForm, employee_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="goal-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} className="rounded-sm" data-testid="goal-title-input" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Weight %</Label><Input type="number" value={goalForm.weight_percent} onChange={(e) => setGoalForm({ ...goalForm, weight_percent: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Target</Label><Input value={goalForm.target} onChange={(e) => setGoalForm({ ...goalForm, target: e.target.value })} className="rounded-sm" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenGoal(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitGoal} className="btn-zenith rounded-sm" data-testid="goal-save-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!selectedCycleObj ? (
        <div className="border border-zinc-200 bg-white p-8 rounded-sm text-center text-sm text-zinc-500">
          No performance cycle yet. Create one to get started.
        </div>
      ) : (
        <>
          <div className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Cycle</div>
            <div className="font-display text-xl font-bold mt-0.5">{selectedCycleObj.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{selectedCycleObj.start_date} → {selectedCycleObj.end_date}</div>
          </div>

          <div className="space-y-3" data-testid={PERF.goalsList}>
            {goals.length === 0 && <div className="border border-zinc-200 bg-white p-8 rounded-sm text-center text-sm text-zinc-500">No goals yet for this cycle.</div>}
            {goals.map((g) => (
              <div key={g.id} className="border border-zinc-200 bg-white p-4 rounded-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-sm bg-emerald-50 grid place-items-center text-emerald-800"><Target size={20} weight="duotone" /></div>
                    <div>
                      {g.employee_name && <div className="text-xs font-mono-plex text-zinc-500">{g.employee_code} · {g.employee_name}</div>}
                      <div className="font-display text-lg font-bold">{g.title}</div>
                      {g.description && <div className="text-sm text-zinc-600 mt-1">{g.description}</div>}
                      {g.target && <div className="text-xs text-zinc-500 mt-1">🎯 {g.target}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300">{g.weight_percent}%</Badge>
                    <Select value={g.status || "on_track"} onValueChange={(v) => updateGoal(g.id, { status: v })}>
                      <SelectTrigger className={`w-[130px] rounded-sm ${STATUS_STYLE[g.status || "on_track"]}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <RatingBlock
                    label={user?.employee_id === g.employee_id ? "My self-rating" : "Self-rating"}
                    value={g.self_rating}
                    editable={user?.employee_id === g.employee_id}
                    onChange={(v) => updateGoal(g.id, { self_rating: v })}
                    testid={`goal-self-${g.id}`}
                  />
                  <RatingBlock
                    label="Manager rating"
                    value={g.manager_rating}
                    editable={["super_admin", "company_admin", "hr", "manager"].includes(user?.role)}
                    onChange={(v) => updateGoal(g.id, { manager_rating: v })}
                    testid={`goal-manager-${g.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RatingBlock({ label, value, editable, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 flex items-center gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            disabled={!editable}
            onClick={() => editable && onChange(n)}
            className={`p-1 transition-colors duration-150 ${editable ? "hover:opacity-70" : "opacity-90"}`}
          >
            <Star size={20} weight={value && n <= value ? "fill" : "regular"} className={value && n <= value ? "text-emerald-700" : "text-zinc-300"} />
          </button>
        ))}
        {value && <span className="text-xs text-zinc-500 ml-1">{value}/5</span>}
      </div>
    </div>
  );
}
